import React, { useContext, useState } from 'react';
import { useQuery } from 'react-query';

import { getCommunity, updateCommunity } from 'apis/CommunityAPI';
import CommunityInfoConfirmationModal from 'components/CommunityInfo/CommunityInfoConfirmationModal';
import { useRootContext } from 'components/layout/RootContext';
import { COMMUNITY_RENTAL_TYPE } from 'constants/rentalTypes';
import { ContractType } from 'types/contracts';

import BasicInfoEditForm from './BasicInfoEditForm';
import { COMMUNITY_CONTRACT_TYPE, CONTRACT_SLUG_PREFIX } from '../../../../constants';
import Loader from '../../../lib/Loader';
import { useEditDataModalContext } from '../../common/edit/EditDataModalContext';
import { CommunityInfoContext } from '../../CommunityInfoContext';

type Props = {
  community?: any;
  disabled?: boolean;
};

const BasicInfoEdit = ({ community, disabled = false }: Props) => {
  const { showMessage } = useRootContext();
  const { communityInfo, communityInfoRefetch } = useContext(CommunityInfoContext);
  const { form, modified, setModified, setLoading, onSuccess } = useEditDataModalContext();

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingValues, setPendingValues] = useState<any>(null);

  const { data, isSuccess, isLoading, refetch } = useQuery({
    queryKey: ['community', 'basic', 'info', communityInfo?.id, community],
    queryFn: () => getCommunity(communityInfo?.id || community?.id),
    enabled: Boolean(communityInfo || community),
    refetchOnWindowFocus: false,
  });

  const handleSubmit = async (values: any) => {
    const rentalTypes: number[] = values.rental_types || [];
    const hasCCRCType = rentalTypes.includes(COMMUNITY_RENTAL_TYPE.CCRC);

    const ccrcContracts = Object.values((values?.contracts ?? {}) as Record<string, ContractType[]>)
      .flatMap((items: ContractType[]) => items)
      .filter((item: ContractType) => Boolean(item) && item.type);

    const payload = {
      ...values,
      rental_types: rentalTypes,
      contracts: hasCCRCType ? ccrcContracts : [],
    };

    setPendingValues(payload);
    setShowConfirmation(true);
  };

  const handleConfirmSubmit = async () => {
    if (!pendingValues) return;

    setLoading(true);

    try {
      await updateCommunity(communityInfo?.id || community?.id, pendingValues).then((response) => {
        showMessage('success', 'Community updated successfully');
        refetch();
        communityInfoRefetch();
        onSuccess(response);
      });
    } catch (error) {
      showMessage('error', 'Failed to update Community information!');
    } finally {
      setLoading(false);
      setShowConfirmation(false);
      setPendingValues(null);
    }
  };

  const handleValuesChanged = () => {
    if (!modified) {
      setModified(true);
    }
  };

  if (isLoading && !isSuccess) return <Loader active />;

  const communityRentalTypes = (data?.rental_types || []).map((type: any) => type.type);

  const communityCCRCContracts = (data?.contracts || [])
    .filter((contract: any) => contract.type != COMMUNITY_CONTRACT_TYPE.RENTAL)
    .reduce((acc: Record<string, any[]>, contract: any) => {
      const key = `${CONTRACT_SLUG_PREFIX}-${contract.type}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push({
        id: contract?.id,
        type: contract.type,
        refund_percentage: contract.refund_percentage,
      });
      return acc;
    }, {});

  return (
    <div style={{ display: 'flex', height: '100%', minWidth: '600px' }}>
      <BasicInfoEditForm
        form={form}
        community={data}
        handleValuesChanged={handleValuesChanged}
        handleSubmit={handleSubmit}
        initialValues={{
          community_id: data?.id,
          comunity_name: data?.comunity_name,
          address: data?.address,
          city: data?.city,
          state: data?.state,
          zip_code: data?.zip_code,
          phone_number: data?.phone_number,
          website: data?.website,
          notes: data?.notes,
          year_opened: data?.year_opened,
          google_map_location: data?.google_map_location,
          a_place_for_mom_id: data?.a_place_for_mom_id,
          caring_url: data?.caring_url,
          rental_types: communityRentalTypes,
          contracts: communityCCRCContracts,
        }}
      />
      <CommunityInfoConfirmationModal
        showCommunityIncentivesConfirmationModal={showConfirmation}
        communityName={communityInfo?.comunity_name}
        onConfirm={handleConfirmSubmit}
        onClose={() => setShowConfirmation(false)}
      />
    </div>
  );
};

export default BasicInfoEdit;
